// @ts-check

/**
 * @file Renders the frontpage task list.
 */

import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import {useLocalSearchParams} from "expo-router"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {memo, useEffect} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {Pressable, ScrollView, View} from "react-native-propforge"
import Modal from "../src/components/modal"
import Pagination from "../src/components/pagination"
import {Task} from "../src/frontend-models/task"
import TaskCard from "../src/components/task-card"
import TaskFilters from "../src/components/tasks/filters"
import {useScreenLoading} from "../src/components/board-layout/screen-loading"
import {DEFAULT_TASK_SORT_KEY, taskSortExpression} from "../src/shared/task-sort"
import useThemeColors, {useThemeName} from "../src/shared/use-theme-colors"
import Text from "@kaspernj/api-maker/build/utils/text"
import ScreenTitle from "../src/components/screen-title"

/** @typedef {Record<string, string | string[] | undefined>} RouteParams */

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

const PAGE_SIZE = 30
const TASK_CARD_PRELOAD_SELECT = {
  Project: ["id", "name"]
}
const TASK_CARD_SELECT = ["canClose", "createdAt", "id", "name", "priority", "projectId", "state"]
const VISIBLE_STATES = ["open", "confirmed", "waiting"]

/** @typedef {{kind: "error" | "success", text: string} | null} FrontpageStatus */

/** @typedef {{[key: string]: unknown}} FrontpageIndexScreenProps */

/**
 * @typedef {object} FrontpageIndexScreenState
 * @property {Task | null} confirmingTask Task awaiting close confirmation.
 * @property {Task | null} confirmingDeleteTask Task awaiting delete confirmation.
 * @property {boolean} loading Whether frontpage tasks are loading.
 * @property {number} page Current page being shown.
 * @property {FrontpageStatus} status Screen status message.
 * @property {{name: string, sortKey: import("../src/shared/task-sort").TaskSortKey, states: string[]}} taskFilters Active task filters.
 * @property {Task[]} tasks Loaded frontpage tasks.
 * @property {number} totalCount Number of matching tasks across all pages.
 */

/**
 * Frontpage screen migrated from the legacy Rails/React root route.
 * @augments {ShapeComponent<FrontpageIndexScreenProps, FrontpageIndexScreenState>}
 */
class FrontpageIndexScreen extends ShapeComponent {
  /** @type {FrontpageIndexScreenState} */
  state = {
    confirmingTask: null,
    confirmingDeleteTask: null,
    loading: true,
    page: 1,
    status: null,
    taskFilters: {
      name: "",
      sortKey: DEFAULT_TASK_SORT_KEY,
      states: []
    },
    tasks: [],
    totalCount: 0
  }
  loadRequestId = 0
  /** @type {RouteParams} */
  params = {}
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** Initializes locale handling and frontpage loading. */
  setup() {
    useLocale()
    this.params = useLocalSearchParams()
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    const page = this.s.page
    const taskFilterName = this.s.taskFilters.name
    const taskFilterSortKey = this.s.taskFilters.sortKey
    const taskFilterStatesKey = this.s.taskFilters.states.join(",")
    useEffect(() => {
      void this.loadFrontpage()
    }, [page, taskFilterName, taskFilterSortKey, taskFilterStatesKey])
    useScreenLoading(this.s.loading && this.s.tasks.length === 0)
  }

  /**
   * Renders the frontpage task list.
   * @returns {ReactNode} Frontpage content.
   */
  render() {
    const {confirmingTask, confirmingDeleteTask, page, status, taskFilters, tasks, totalCount} = this.s
    const taskFiltersActive = Boolean(
      taskFilters.name.trim() ||
      taskFilters.states.length > 0 ||
      taskFilters.sortKey !== DEFAULT_TASK_SORT_KEY
    )
    return (
      <ScrollView
        contentContainerStyle={styles.screenContent ||= {
          flexGrow: 1,
          padding: 24,
          paddingBottom: 48
        }}
        keyboardShouldPersistTaps="handled"
        style={styles.screen ||= {
          flex: 1
        }}
        testID="frontpageScreen"
      >
        <ScreenTitle>{_("Tasks")}</ScreenTitle>
        <Text
          style={styles[`title-${this.tt.themeName}`] ||= {
            fontSize: 24,
            fontWeight: 700,
            marginBottom: 16
          }}
          testID="frontpageScreen/title"
        >
          {_("Tasks")}
        </Text>
        {status &&
          <Text
            style={styles[`status-${this.tt.themeName}-${status.kind}`] ||= {
              color: status.kind === "error" ? this.tt.colors.dangerText : this.tt.colors.successText,
              fontWeight: 600,
              marginBottom: 16
            }}
            testID="frontpageScreen/status"
          >
            {status.text}
          </Text>
        }
        <TaskFilters
          onChangeValue={this.tt.onTaskFiltersChangeValue}
          statePlaceholder={_("Open, confirmed, and waiting")}
          testID="frontpageScreen/filters"
          value={taskFilters}
        />
        <Modal
          cardStyle={styles[`confirmModalCard-${this.tt.themeName}`] ||= {
            borderColor: this.tt.colors.danger,
            maxWidth: 420,
            padding: 16
          }}
          isVisible={Boolean(confirmingTask)}
          onRequestClose={this.tt.onCancelClosePress}
          testID="frontpageScreen/closeConfirm"
        >
          <Text
            style={styles[`confirmTitle-${this.tt.themeName}`] ||= {
              color: this.tt.colors.dangerText,
              fontSize: 16,
              fontWeight: 700,
              marginBottom: 12
            }}
            testID="frontpageScreen/closeConfirm/title"
          >
            {_("Close task")}
          </Text>
          <Text
            style={styles[`confirmText-${this.tt.themeName}`] ||= {
              marginBottom: 12
            }}
            testID="frontpageScreen/closeConfirm/text"
          >
            {_("Are you sure that you want to close this task?")}
          </Text>
          <View
            style={styles.confirmActions ||= {
              flexDirection: "row",
              gap: 8
            }}
            testID="frontpageScreen/closeConfirm/actions"
          >
            <Pressable
              onPress={this.tt.onCancelClosePress}
              style={styles[`cancelButton-${this.tt.themeName}`] ||= {
                alignItems: "center",
                backgroundColor: this.tt.colors.surfaceMuted,
                borderRadius: 8,
                flexDirection: "row",
                paddingHorizontal: 12,
                paddingVertical: 10
              }}
              testID="frontpageScreen/closeConfirm/cancelButton"
            >
              <View
                style={styles.cancelButtonIcon ||= {
                  marginRight: 8,
                  width: 14
                }}
                testID="frontpageScreen/closeConfirm/cancelButton/icon"
              >
                <FontAwesome6 color={this.tt.colors.text} name="xmark" size={14} />
              </View>
              <Text
                style={styles[`cancelButtonText-${this.tt.themeName}`] ||= {
                  fontWeight: 600
                }}
                testID="frontpageScreen/closeConfirm/cancelButton/text"
              >
                {_("Cancel")}
              </Text>
            </Pressable>
            <Pressable
              onPress={this.tt.onConfirmClosePress}
              style={styles[`confirmButton-${this.tt.themeName}`] ||= {
                alignItems: "center",
                backgroundColor: this.tt.colors.danger,
                borderRadius: 8,
                flexDirection: "row",
                paddingHorizontal: 12,
                paddingVertical: 10
              }}
              testID="frontpageScreen/closeConfirm/confirmButton"
            >
              <View
                style={styles.confirmButtonIcon ||= {
                  marginRight: 8,
                  width: 14
                }}
                testID="frontpageScreen/closeConfirm/confirmButton/icon"
              >
                <FontAwesome6 color={this.tt.colors.onPrimary} name="circle-check" size={14} />
              </View>
              <Text
                style={styles[`confirmButtonText-${this.tt.themeName}`] ||= {
                  color: this.tt.colors.onPrimary,
                  fontWeight: 600
                }}
                testID="frontpageScreen/closeConfirm/confirmButton/text"
              >
                {_("Close")}
              </Text>
            </Pressable>
          </View>
        </Modal>
        <Modal
          cardStyle={styles[`confirmModalCard-${this.tt.themeName}`]}
          isVisible={Boolean(confirmingDeleteTask)}
          onRequestClose={this.tt.onCancelDeletePress}
          testID="frontpageScreen/deleteConfirm"
        >
          <Text
            style={styles[`confirmTitle-${this.tt.themeName}`]}
            testID="frontpageScreen/deleteConfirm/title"
          >
            {_("Delete task")}
          </Text>
          <Text
            style={styles[`confirmText-${this.tt.themeName}`]}
            testID="frontpageScreen/deleteConfirm/text"
          >
            {_("Are you sure that you want to delete this task?")}
          </Text>
          <View
            style={styles.confirmActions}
            testID="frontpageScreen/deleteConfirm/actions"
          >
            <Pressable
              onPress={this.tt.onCancelDeletePress}
              style={styles[`cancelButton-${this.tt.themeName}`]}
              testID="frontpageScreen/deleteConfirm/cancelButton"
            >
              <View
                style={styles.cancelButtonIcon}
                testID="frontpageScreen/deleteConfirm/cancelButton/icon"
              >
                <FontAwesome6 color={this.tt.colors.text} name="xmark" size={14} />
              </View>
              <Text
                style={styles[`cancelButtonText-${this.tt.themeName}`]}
                testID="frontpageScreen/deleteConfirm/cancelButton/text"
              >
                {_("Cancel")}
              </Text>
            </Pressable>
            <Pressable
              onPress={this.tt.onConfirmDeletePress}
              style={styles[`confirmButton-${this.tt.themeName}`]}
              testID="frontpageScreen/deleteConfirm/confirmButton"
            >
              <View
                style={styles.confirmButtonIcon}
                testID="frontpageScreen/deleteConfirm/confirmButton/icon"
              >
                <FontAwesome6 color={this.tt.colors.onPrimary} name="trash" size={14} />
              </View>
              <Text
                style={styles[`confirmButtonText-${this.tt.themeName}`]}
                testID="frontpageScreen/deleteConfirm/confirmButton/text"
              >
                {_("Delete")}
              </Text>
            </Pressable>
          </View>
        </Modal>
        {!this.s.loading && tasks.length === 0 &&
          <Text
            style={styles[`emptyText-${this.tt.themeName}`] ||= {
              color: this.tt.colors.textMuted,
              fontSize: 15
            }}
            testID="frontpageScreen/emptyText"
          >
            {taskFiltersActive ? _("No matching tasks found") : _("No tasks found")}
          </Text>
        }
        <View testID={`frontpageScreen/tasksList/${taskFilters.sortKey}/${this.s.loading ? "loading" : "loaded"}`}>
          {tasks.map((task) =>
            <TaskCard
              cardTestID={`frontpageScreen/task-${task.id()}`}
              key={task.id()}
              onClosePress={this.tt.onTaskClosePress}
              onDeletePress={this.tt.onTaskDeletePress}
              params={this.params}
              showCreatedAt
              task={task}
              testIDPrefix="frontpageScreen"
            />
          )}
        </View>
        <Pagination
          onPageChange={this.tt.onPageChange}
          page={page}
          pageSize={PAGE_SIZE}
          testID="frontpageScreen/pagination"
          totalCount={totalCount}
        />
      </ScrollView>
    )
  }

  /**
   * Loads frontpage tasks.
   * @returns {Promise<void>} Resolves when frontpage loading completes.
   */
  loadFrontpage = async () => {
    const requestId = ++this.loadRequestId
    const {page, taskFilters} = this.s
    this.s.loading = true
    try {
      const nameFilter = taskFilters.name.trim()
      /** @type {Record<string, unknown>} */
      const ransackParams = {
        s: taskSortExpression(taskFilters.sortKey)
      }
      if (nameFilter) ransackParams.nameCont = nameFilter
      if (taskFilters.states.length > 0) {
        ransackParams.stateIn = taskFilters.states
      } else {
        ransackParams.stateIn = VISIBLE_STATES
      }
      const baseQuery = Task.ransack(ransackParams)
      const [tasks, totalCount] = await Promise.all([
        baseQuery
          .clone()
          .preload("project")
          .select(TASK_CARD_SELECT)
          .select(TASK_CARD_PRELOAD_SELECT)
          .page(page)
          .perPage(PAGE_SIZE)
          .toArray(),
        baseQuery.clone().count()
      ])
      if (this.loadRequestId !== requestId) return
      const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
      if (page > totalPages) {
        // Page is past the new tail (e.g., the only task on the last page
        // was deleted). Snap back to the new last page; the page-effect
        // re-runs loadFrontpage automatically. Keep `loading: true` so we
        // don't briefly render "No tasks found" between loads.
        this.s.totalCount = totalCount
        this.s.page = totalPages
        return
      }
      this.s.tasks = tasks
      this.s.totalCount = totalCount
      this.s.loading = false
    } catch (error) {
      if (this.loadRequestId !== requestId) return
      const message = error instanceof Error ? error.message : String(error)
      this.s.status = {
        kind: "error",
        text:
          message === "Forbidden"
            ? _("You do not have access to this page")
            : message
      }
      this.s.loading = false
    }
  }

  /**
   * Updates the active page.
   * @param {number} nextPage Page to load.
   */
  onPageChange = (nextPage) => {
    if (nextPage === this.s.page) return
    this.s.page = nextPage
  }

  /**
   * Applies frontpage task filters and resets pagination.
   * @param {{name: string, sortKey: import("../src/shared/task-sort").TaskSortKey, states: string[]}} nextFilters Filter values requested by the shared controls.
   * @returns {void} Nothing.
   */
  onTaskFiltersChangeValue = (nextFilters) => {
    if (
      nextFilters.name === this.s.taskFilters.name &&
      nextFilters.sortKey === this.s.taskFilters.sortKey &&
      sameStringArray(nextFilters.states, this.s.taskFilters.states)
    ) {
      return
    }

    this.s.page = 1
    this.s.loading = true
    this.s.taskFilters = nextFilters
  }

  /**
   * Opens close confirmation for a task.
   * @param {Task} task Task to close.
   */
  onTaskClosePress = (task) => {
    this.s.confirmingTask = task
    this.s.status = null
  }

  /** Closes task close confirmation. */
  onCancelClosePress = () => {
    this.s.confirmingTask = null
  }

  /**
   * Closes the confirmed task.
   * @returns {Promise<void>} Resolves when close handling completes.
   * @rejects {Error} When no task has been selected.
   */
  onConfirmClosePress = async () => {
    const {confirmingTask} = this.s
    if (!confirmingTask) {
      throw new Error("Expected a confirming task before closing")
    }
    try {
      await confirmingTask.close()
      this.s.confirmingTask = null
      await this.loadFrontpage()
      this.s.status = {
        kind: "success",
        text: _("The task was closed")
      }
    } catch (error) {
      this.s.confirmingTask = null
      this.s.status = {
        kind: "error",
        text: error instanceof Error ? error.message : String(error)
      }
    }
  }

  /**
   * Opens delete confirmation for a task.
   * @param {Task} task Task to delete.
   */
  onTaskDeletePress = (task) => {
    this.s.confirmingDeleteTask = task
    this.s.status = null
  }

  /** Closes task delete confirmation. */
  onCancelDeletePress = () => {
    this.s.confirmingDeleteTask = null
  }

  /**
   * Deletes the confirmed task.
   * @returns {Promise<void>} Resolves when delete handling completes.
   * @rejects {Error} When no task has been selected.
   */
  onConfirmDeletePress = async () => {
    const {confirmingDeleteTask} = this.s
    if (!confirmingDeleteTask) {
      throw new Error("Expected a confirming task before deleting")
    }
    try {
      await confirmingDeleteTask.destroy()
      this.s.confirmingDeleteTask = null
      await this.loadFrontpage()
      this.s.status = {
        kind: "success",
        text: _("The task was deleted")
      }
    } catch (error) {
      this.s.confirmingDeleteTask = null
      this.s.status = {
        kind: "error",
        text: error instanceof Error ? error.message : String(error)
      }
    }
  }
}

/** @type {NamedExoticComponent<FrontpageIndexScreenProps>} */
const FrontpageIndexScreenComponent = memo(shapeComponent(FrontpageIndexScreen))

export default FrontpageIndexScreenComponent

/**
 * Compares two ordered string arrays.
 * @param {string[]} first First array to compare.
 * @param {string[]} second Second array to compare.
 * @returns {boolean} True when both arrays contain the same values in order.
 */
function sameStringArray(first, second) {
  if (first.length !== second.length) return false
  for (let index = 0; index < first.length; index++) {
    if (first[index] !== second[index]) return false
  }
  return true
}
